import { useRoute, Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, ExternalLink, MapPin, Video, Ticket, Clock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { URBAN_FILM_FESTIVAL_SCHEDULE, URBAN_FILM_FESTIVAL_SOURCE } from "@/data/urbanFilmFestival";
import ismDiamond from "@assets/IMG_3411_1779819410701.png";
import { format, parseISO, isValid } from "date-fns";

export default function UrbanFilmFestivalDate() {
  const [match, params] = useRoute("/urban-film-festival-miami/:date");
  const dateStr = params?.date || "";
  
  const entries = URBAN_FILM_FESTIVAL_SCHEDULE[dateStr];
  const parsedDate = parseISO(dateStr);
  const isDateValid = isValid(parsedDate);
  const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  const dateArtwork = dateStr === "2026-09-05" ? `${base}/transcend-film.jpg` : `${base}/uff-logo.png`;

  if (!match || !entries || !isDateValid) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="text-center max-w-md space-y-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold uppercase tracking-widest">Schedule Not Found</h1>
          <p className="text-muted-foreground">
            We couldn't find any festival programming for the date you requested.
          </p>
          <Link href="/urban-film-festival-miami" className="inline-block mt-4">
            <Button variant="outline" className="gap-2 border-white/20">
              <ArrowLeft className="w-4 h-4" />
              Back to Overview
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 right-0 opacity-[0.03] pointer-events-none z-0">
        <img src={ismDiamond} alt="Background" className="w-[1200px] h-[1200px] object-cover mix-blend-screen" />
      </div>
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-primary/10 blur-[150px] rounded-full pointer-events-none z-0" />

      {/* Header */}
      <div className="relative z-10 sticky top-16 bg-background/80 backdrop-blur-xl border-b border-white/10 pt-8 pb-6">
        <div className="container mx-auto px-4 max-w-5xl">
          <Link href="/urban-film-festival-miami" className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary transition-colors uppercase tracking-widest mb-6">
            <ArrowLeft className="w-4 h-4" />
            Festival Overview
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase leading-tight mb-2">
                {format(parsedDate, "EEEE")} Schedule
              </h1>
              <div className="flex items-center gap-3 text-muted-foreground font-mono text-sm">
                <Calendar className="w-4 h-4 text-primary" />
                {format(parsedDate, "MMMM do, yyyy")}
              </div>
            </div>

            <img
              src={dateArtwork}
              alt={dateStr === "2026-09-05" ? "Transcend, an official Urban Film Festival 2026 selection" : "Urban Film Festival logo"}
              className={`hidden lg:block w-28 h-28 rounded-xl border border-white/10 bg-white object-center ${dateStr === "2026-09-05" ? "object-cover object-top" : "object-contain p-2"}`}
            />
            
            <a href={URBAN_FILM_FESTIVAL_SOURCE} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="gap-2 text-xs font-mono uppercase tracking-widest border-white/20 hover:border-primary hover:text-primary">
                <ExternalLink className="w-3.5 h-3.5" />
                View on Eventive
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Schedule List */}
      <div className="relative z-10 container mx-auto px-4 py-12 max-w-5xl">
        <div className="space-y-4">
          {entries.map((entry, idx) => (
            <motion.div
              key={`${entry.time}-${entry.title}-${idx}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: Math.min(idx * 0.05, 0.5) }}
              className="group relative bg-card/30 hover:bg-card/60 border border-white/5 hover:border-white/20 backdrop-blur-md rounded-xl p-6 transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                
                {/* Time */}
                <div className="md:w-32 shrink-0 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span className="font-mono text-lg font-bold text-white whitespace-nowrap">
                    {entry.time}
                  </span>
                </div>
                
                {/* Details */}
                <div className="flex-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="px-2.5 py-1 rounded bg-white/5 border border-white/10 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      {entry.category}
                    </span>
                    {entry.ticketed && (
                      <span className="px-2.5 py-1 rounded bg-primary/10 border border-primary/20 text-[10px] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5">
                        <Ticket className="w-3 h-3" /> Ticketed
                      </span>
                    )}
                  </div>
                  
                  <h2 className="text-xl md:text-2xl font-bold uppercase tracking-tight leading-snug">
                    {entry.title}
                  </h2>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span>{entry.venue}</span>
                  </div>
                </div>

              </div>
            </motion.div>
          ))}
        </div>
        
        {/* End of schedule */}
        <div className="mt-16 text-center pb-8 border-t border-white/10 pt-12">
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-widest mb-6">
            End of programming for {format(parsedDate, "MMM do")}.
          </p>
          <a href={URBAN_FILM_FESTIVAL_SOURCE} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="font-bold uppercase tracking-widest px-8 bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
              <Ticket className="w-4 h-4" />
              Get Tickets on Eventive
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
